import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthContext } from '@/contexts/auth-context'
import { getVerifiedUser } from '@/lib/api/auth'

const HoomiLogo = '/assets/hoomi-logo.png'

const ALL_APPS = [
  {
    role: ['admin', 'partner', 'user'],
    name: 'Hoomi Developer',
    description: 'Manage your micro-apps, workspaces, and developer settings.',
    url: import.meta.env.VITE_DEVELOPER_APP_URL,
    icon: (
      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    role: ['admin'],
    name: 'Hoomi Admin',
    description: 'Access the admin panel to manage users, content, and platform settings.',
    url: import.meta.env.VITE_ADMIN_APP_URL,
    icon: (
      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
  },
]

export default function Authorized() {
  const navigate = useNavigate()
  const { logout, jwtToken } = useAuthContext()
  const [role, setRole] = useState<string | null>(null)
  const [roleLoading, setRoleLoading] = useState(true)

  useEffect(() => {
    if (!jwtToken) {
      setRoleLoading(false)
      return
    }
    getVerifiedUser(jwtToken)
      .then(data => setRole(data?.role ?? null))
      .finally(() => setRoleLoading(false))
  }, [jwtToken])

  const handleLogout = async () => {
    await logout()
    navigate('/auth')
  }

  const visibleApps = ALL_APPS.filter(app => role && app.role.includes(role))

  return (
    <div className="bg-black flex min-h-svh flex-col text-white relative">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0 pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Header */}
      <header className="relative z-10 w-full px-6 py-4 flex items-center justify-between border-b border-purple-500/10">
        <Link to="/" className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-red-600/80 hover:bg-red-600 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign Out
        </button>
      </header>

      {/* Content */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-8 p-6 md:p-10">
        <div className="text-center space-y-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-purple-500/20 flex items-center justify-center border-4 border-purple-500">
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">You're signed in</h1>
          <p className="text-sm text-white/60">Your session is active across all Hoomi apps.</p>
        </div>

        {roleLoading ? (
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
        ) : visibleApps.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-xl">
            {visibleApps.map((app) => (
              <a
                key={app.name}
                href={app.url}
                className="group flex flex-col gap-3 bg-black/30 backdrop-blur-lg rounded-xl border border-purple-500/20 shadow-xl shadow-purple-500/10 p-6 hover:border-purple-500/50 hover:bg-purple-500/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    {app.icon}
                  </div>
                  <span className="font-semibold text-white group-hover:text-purple-300 transition-colors">
                    {app.name}
                  </span>
                </div>
                <p className="text-xs text-white/50 leading-relaxed">{app.description}</p>
                <div className="flex items-center gap-1 text-xs text-purple-400/70 group-hover:text-purple-400 transition-colors mt-auto">
                  Open
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="w-full max-w-sm bg-black/30 backdrop-blur-lg rounded-xl border border-purple-500/20 p-6 text-center space-y-2">
            <p className="text-white/70 text-sm">No web apps are assigned to your account.</p>
            <p className="text-white/40 text-xs">Continue using Hoomi on the mobile app.</p>
          </div>
        )}

        <p className="text-xs text-white/30 text-center">
          Signing out will clear your session on all Hoomi apps.
        </p>
      </main>
    </div>
  )
}
